import { Helmet } from "react-helmet-async";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import StorageIcon from "@mui/icons-material/Storage";
import TimelineIcon from "@mui/icons-material/Timeline";
import { getCurrentUser, getUserPermissions } from "src/utils/permissionsUtils";
import { del, get, post } from "src/utils/networkUtils";
import ScenarioTestsPanel from "src/sections/simulator/ScenarioTestsPanel";
import SeasonReplayPanel from "src/sections/simulator/SeasonReplayPanel";
import {
  FEATURE_FLAG_KEYS,
  listFeatureFlags,
  setFeatureFlag,
} from "src/utils/featureFlagsApi";

const FALLBACK_SCENARIO = {
  match: {
    id: "sim-world-cup-001",
    durationMinutes: 90,
  },
  teams: [
    { id: "home", name: "Home XI" },
    { id: "away", name: "Away XI" },
  ],
  players: [
    { id: "home-gk", name: "Home Goalkeeper", teamId: "home", position: "GK" },
    { id: "home-def", name: "Home Defender", teamId: "home", position: "DEF" },
    {
      id: "home-mid",
      name: "Home Midfielder",
      teamId: "home",
      position: "MID",
    },
    { id: "home-fwd", name: "Home Forward", teamId: "home", position: "FWD" },
    { id: "away-gk", name: "Away Goalkeeper", teamId: "away", position: "GK" },
    { id: "away-def", name: "Away Defender", teamId: "away", position: "DEF" },
    { id: "away-fwd", name: "Away Forward", teamId: "away", position: "FWD" },
  ],
  events: [
    {
      minute: 12,
      type: "goal",
      playerId: "home-fwd",
      relatedPlayerId: "home-mid",
    },
    { minute: 16, type: "shot_on_target", playerId: "home-fwd" },
    { minute: 29, type: "save", playerId: "away-gk", value: 2 },
    { minute: 61, type: "yellow_card", playerId: "away-def" },
    { minute: 70, type: "dribble", playerId: "home-mid", value: 3 },
  ],
  fantasyTeams: [
    {
      id: "fantasy-alpha",
      name: "Captain Goal Test",
      starterPlayerIds: ["home-def", "home-mid", "home-fwd"],
      captainPlayerId: "home-fwd",
      viceCaptainPlayerId: "home-mid",
    },
    {
      id: "fantasy-goal-bonus",
      name: "Goal Bonus Test",
      starterPlayerIds: ["home-def", "home-mid", "home-fwd"],
      captainPlayerId: "home-fwd",
      viceCaptainPlayerId: "home-mid",
      booster: "goal_bonus",
    },
  ],
};




const GAMEWEEK_STATUSES = ["upcoming", "open", "locked", "live", "finalised"];

const SIMULATOR_MODE_STORAGE_KEY = "dreamteam-admin-match-simulator-enabled";

function readSimulatorModeEnabled() {
  if (typeof window === "undefined") return false;

  try {
    return window.localStorage.getItem(SIMULATOR_MODE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function persistSimulatorModeEnabled(enabled) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      SIMULATOR_MODE_STORAGE_KEY,
      enabled ? "true" : "false"
    );
  } catch {
    // Local storage can be unavailable in embedded browser contexts.
  }
}

function formatJson(value) {
  return JSON.stringify(value, null, 2);
}

function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "-";
  return new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 }).format(
    Number(value)
  );
}

function titleize(value) {
  return String(value || "-")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getSimulationPayload(responseBody) {
  return responseBody?.data ?? null;
}

function formatMatchLabel(match) {
  const home =
    match?.homeContestant?.short_name || match?.homeContestant?.name || "Home";
  const away =
    match?.awayContestant?.short_name || match?.awayContestant?.name || "Away";
  const gameweek = match?.gameweek ? `GW ${match.gameweek}` : "No GW";
  return `${home} vs ${away} - ${gameweek} - ${match?.status || "unknown"}`;
}

function parseScenarioText(text) {
  try {
    const scenario = JSON.parse(text);
    return { scenario, error: null };
  } catch (err) {
    return {
      scenario: null,
      error: err instanceof Error ? err.message : "Invalid scenario JSON.",
    };
  }
}

function getScenarioTeams(scenario) {
  return Array.isArray(scenario?.teams) ? scenario.teams : [];
}

function getScenarioPlayers(scenario) {
  const teamsById = new Map(
    getScenarioTeams(scenario).map((team) => [team.id, team.name])
  );

  return (Array.isArray(scenario?.players) ? scenario.players : [])
    .map((player) => ({
      ...player,
      teamName: teamsById.get(player.teamId) || player.teamId || "No team",
    }))
    .sort((a, b) =>
      `${a.teamName} ${a.position || ""} ${a.name || a.id}`.localeCompare(
        `${b.teamName} ${b.position || ""} ${b.name || b.id}`
      )
    );
}

function getScenarioMatchId(scenario, selectedMatchId) {
  return selectedMatchId.trim() || scenario?.match?.id || "";
}

function TableCard({ title, children, action }) {
  return (
    <Card sx={{ height: "100%" }}>
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </Card>
  );
}

TableCard.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  action: PropTypes.node,
};

TableCard.defaultProps = {
  action: null,
};

function ResultMetric({ label, value }) {
  return (
    <Grid item xs={12} sm={4}>
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h4">{value}</Typography>
        </CardContent>
      </Card>
    </Grid>
  );
}

ResultMetric.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

function ScoreTable({ simulation }) {
  return (
    <TableCard title="Score">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Team</TableCell>
              <TableCell align="right">Goals</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(simulation?.score ?? []).map((team) => (
              <TableRow key={team.teamId}>
                <TableCell>{team.name}</TableCell>
                <TableCell align="right">{formatNumber(team.goals)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
}

ScoreTable.propTypes = {
  simulation: PropTypes.shape({
    score: PropTypes.arrayOf(
      PropTypes.shape({
        teamId: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        goals: PropTypes.number.isRequired,
      })
    ),
  }),
};

ScoreTable.defaultProps = {
  simulation: null,
};

function FantasyTeamTable({ teams }) {
  return (
    <TableCard title="Fantasy Teams">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Team</TableCell>
              <TableCell>Booster</TableCell>
              <TableCell>Captain</TableCell>
              <TableCell align="right">Players</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(teams ?? []).map((team) => (
              <TableRow key={team.fantasyTeamId}>
                <TableCell>{team.name || team.fantasyTeamId}</TableCell>
                <TableCell>
                  {team.booster ? (
                    <Chip size="small" label={team.booster} />
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>{team.captainPlayerId || "-"}</TableCell>
                <TableCell align="right">
                  {formatNumber(team.playersCounted)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(team.totalPoints)}
                </TableCell>
              </TableRow>
            ))}
            {(teams ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No fantasy teams in this simulation.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
}

FantasyTeamTable.propTypes = {
  teams: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

function PlayerPointsTable({ players }) {
  return (
    <TableCard title="Player Points">
      <TableContainer sx={{ maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Player</TableCell>
              <TableCell>Position</TableCell>
              <TableCell align="right">Minutes</TableCell>
              <TableCell align="right">Points</TableCell>
              <TableCell>Breakdown</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(players ?? []).map((player) => (
              <TableRow key={player.playerId} hover>
                <TableCell>{player.name || player.playerId}</TableCell>
                <TableCell>{player.position}</TableCell>
                <TableCell align="right">
                  {formatNumber(player.stats?.minutes_played ?? 0)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(player.points)}
                </TableCell>
                <TableCell>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    useFlexGap
                    flexWrap="wrap"
                  >
                    {Object.entries(player.breakdown || {}).map(
                      ([key, value]) => (
                        <Chip
                          key={`${player.playerId}-${key}`}
                          size="small"
                          variant="outlined"
                          label={`${titleize(key)} ${formatNumber(value)}`}
                        />
                      )
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {(players ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Run a simulation to view player points.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
}

PlayerPointsTable.propTypes = {
  players: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

function EventsTable({ events }) {
  return (
    <TableCard title="Event Timeline">
      <TableContainer sx={{ maxHeight: 300 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="right">Minute</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Player</TableCell>
              <TableCell align="right">Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(events ?? []).map((event, index) => (
              <TableRow key={`${event.minute}-${event.type}-${index}`}>
                <TableCell align="right">
                  {formatNumber(event.minute)}
                </TableCell>
                <TableCell>{titleize(event.type)}</TableCell>
                <TableCell>{event.playerId || "-"}</TableCell>
                <TableCell align="right">
                  {formatNumber(event.value ?? 1)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
}

EventsTable.propTypes = {
  events: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

function BackendScoringResult({ result }) {
  if (!result) return null;

  const { persistence = {}, scoring = {} } = result;
  const teams = scoring.team_results ?? [];

  return (
    <Grid container spacing={3} sx={{ mt: 0 }}>
      <Grid item xs={12}>
        <TableCard title="Backend Scoring Result">
          <TableContainer>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Match</TableCell>
                  <TableCell>
                    {persistence.matchId || scoring.match_id || "-"}
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>{persistence.status || "-"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Score</TableCell>
                  <TableCell>
                    {formatNumber(persistence.homeScore)} -{" "}
                    {formatNumber(persistence.awayScore)}
                  </TableCell>
                  <TableCell>Stats persisted</TableCell>
                  <TableCell>
                    {formatNumber(persistence.matchStatUpserts)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Players applied</TableCell>
                  <TableCell>
                    {formatNumber(persistence.playersApplied)}
                  </TableCell>
                  <TableCell>Players scored</TableCell>
                  <TableCell>{formatNumber(scoring.players_scored)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Teams updated</TableCell>
                  <TableCell>{formatNumber(scoring.teams_updated)}</TableCell>
                  <TableCell>Replay cleared</TableCell>
                  <TableCell>
                    {formatNumber(persistence.clearedMatchStats)} stats /{" "}
                    {formatNumber(persistence.clearedPlayerMatchdays)} player
                    rows
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Match events</TableCell>
                  <TableCell>
                    {formatNumber(persistence.matchEventsInserted)}
                  </TableCell>
                  <TableCell>Team stats</TableCell>
                  <TableCell>
                    {formatNumber(persistence.teamStatsInserted)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </TableCard>
      </Grid>

      <Grid item xs={12}>
        <TableCard title="Updated Fantasy Teams">
          <TableContainer sx={{ maxHeight: 340 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Fantasy Team</TableCell>
                  <TableCell align="right">Previous</TableCell>
                  <TableCell align="right">New</TableCell>
                  <TableCell align="right">Delta</TableCell>
                  <TableCell align="right">Players</TableCell>
                  <TableCell>Captain</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teams.map((team) => (
                  <TableRow key={team.fantasy_team_id} hover>
                    <TableCell>
                      {team.team_name || team.fantasy_team_id}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(team.previous_points)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(team.new_points)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(team.delta)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(team.players_counted)}
                    </TableCell>
                    <TableCell>
                      {team.captain_applied ? "Applied" : "-"}
                    </TableCell>
                  </TableRow>
                ))}
                {teams.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No fantasy teams were updated. Create a team with players
                      from this match first.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableCard>
      </Grid>
    </Grid>
  );
}

BackendScoringResult.propTypes = {
  result: PropTypes.shape({}),
};

BackendScoringResult.defaultProps = {
  result: null,
};

export default function MatchSimulatorPage() {
  const currentUser = getCurrentUser();
  const { isSuperAdmin } = getUserPermissions();
  const canAccess = currentUser?.role === "super_admin" || isSuperAdmin;

  const [simulatorModeEnabled, setSimulatorModeEnabled] = useState(
    readSimulatorModeEnabled
  );
  const [capabilities, setCapabilities] = useState(null);
  const [loadingCapabilities, setLoadingCapabilities] = useState(false);
  const [finalDelayDisabled, setFinalDelayDisabled] = useState(false);
  const [loadingDelayFlag, setLoadingDelayFlag] = useState(false);
  const [savingDelayFlag, setSavingDelayFlag] = useState(false);
  const [scenarioText, setScenarioText] = useState(
    formatJson(FALLBACK_SCENARIO)
  );
  const [simulation, setSimulation] = useState(null);
  const [matches, setMatches] = useState([]);
  const [selectedMatchId, setSelectedMatchId] = useState("");
  const [gameweeks, setGameweeks] = useState([]);
  const [selectedGameweekId, setSelectedGameweekId] = useState("");
  const [loadingGameweeks, setLoadingGameweeks] = useState(false);
  const [savingGameweek, setSavingGameweek] = useState(false);
  const [gameweekDraft, setGameweekDraft] = useState({
    number: "",
    name: "",
    status: "open",
    budgetInitial: 100,
  });
  const [loadingExample, setLoadingExample] = useState(false);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [resetScope, setResetScope] = useState("matchday");
  const [resetting, setResetting] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [session, setSession] = useState(null);
  const [sessionWorking, setSessionWorking] = useState(false);
  const [scenarioWorking, setScenarioWorking] = useState(false);
  const [simulatorTab, setSimulatorTab] = useState("tests");
  const [scenarioMinute, setScenarioMinute] = useState(1);
  const [, setLastLifecycleResult] = useState(null);
  const [, setLifecycleDraft] = useState({
    matchStatus: "fixture",
    matchdayStatus: "upcoming",
    gameweekStatus: "open",
    kickoffAt: "",
    startsAt: "",
    locksAt: "",
    endsAt: "",
  });
  const [, setEventDraft] = useState({
    minute: 1,
    type: "goal",
    teamId: "",
    playerId: "",
    relatedPlayerId: "",
    value: 1,
    statKey: "",
  });
  const [, setLineups] = useState({
    home: {
      formation: "",
      startingXi: [],
      formationPlace: [],
      substitutes: [],
    },
    away: {
      formation: "",
      startingXi: [],
      formationPlace: [],
      substitutes: [],
    },
  });
  const [, setLoadingLineups] = useState(false);

  const parsedScenario = useMemo(
    () => parseScenarioText(scenarioText),
    [scenarioText]
  );
  const scenario = parsedScenario.scenario;
  const scenarioTeams = useMemo(() => getScenarioTeams(scenario), [scenario]);
  const scenarioPlayers = useMemo(
    () => getScenarioPlayers(scenario),
    [scenario]
  );
  const effectiveMatchId = getScenarioMatchId(scenario, selectedMatchId);
  const selectedMatch = useMemo(
    () => matches.find((match) => match.id === selectedMatchId),
    [matches, selectedMatchId]
  );
  const selectedMatchTournamentCalendarId =
    selectedMatch?.tournament_calendar_id || "";
  const canRunSimulator = capabilities?.canRunSimulation === true;
  const canUseSimulator = simulatorModeEnabled && canRunSimulator;
  const canApplySimulator =
    canUseSimulator && capabilities?.canApplySimulation === true;

  const metrics = useMemo(
    () => [
      {
        label: "Match",
        value: simulation?.matchId || session?.scenario?.match?.id || "-",
      },
      {
        label: "Players",
        value: formatNumber(simulation?.playerResults?.length ?? 0),
      },
      { label: "Events", value: formatNumber(simulation?.events?.length ?? 0) },
    ],
    [simulation, session]
  );

  const setScenarioFromObject = useCallback((nextScenario) => {
    setScenarioText(formatJson(nextScenario ?? FALLBACK_SCENARIO));
    setSession(null);
    setSimulation(null);
    setLastLifecycleResult(null);
  }, []);

  const updateSessionState = useCallback((nextSession) => {
    setSession(nextSession);
    if (nextSession?.scenario) {
      setScenarioText(formatJson(nextSession.scenario));
    }
    setSimulation(nextSession?.result ?? null);
    setApplyResult(null);
  }, []);

  const loadCapabilities = useCallback(async () => {
    setLoadingCapabilities(true);

    try {
      const body = await get("/admin/match-simulator/capabilities");
      if (!body?.success) {
        throw new Error(
          body?.error?.message || "Unable to check simulator capability."
        );
      }
      setCapabilities(getSimulationPayload(body));
    } catch {
      setCapabilities({
        environment: "production",
        simulatorUrlConfigured: false,
        usingDefaultLocalSimulator: true,
        canRunSimulation: false,
        canApplySimulation: false,
      });
      setNotice("Unable to confirm simulator backend capability.");
    } finally {
      setLoadingCapabilities(false);
    }
  }, []);

  const handleSimulatorModeChange = (event) => {
    const enabled = event.target.checked;

    if (enabled && !canRunSimulator) {
      setNotice(
        "Simulator backend is not configured for this environment yet."
      );
      return;
    }

    setSimulatorModeEnabled(enabled);
    persistSimulatorModeEnabled(enabled);
    setError(null);
    setNotice(
      enabled
        ? "Simulator mode enabled. Use this only for controlled test matches."
        : "Simulator mode disabled."
    );

    if (!enabled) {
      setSession(null);
      setSimulation(null);
      setApplyResult(null);
      setLastLifecycleResult(null);
    }
  };

  const loadFinalDelayFlag = useCallback(async () => {
    setLoadingDelayFlag(true);
    try {
      const flags = await listFeatureFlags();
      const flag = flags.find(
        (item) => item.key === FEATURE_FLAG_KEYS.DISABLE_FINAL_POINTS_DELAY
      );
      setFinalDelayDisabled(flag?.is_enabled === true);
    } catch {
      setNotice("Unable to load the scoring buffer setting.");
    } finally {
      setLoadingDelayFlag(false);
    }
  }, []);

  const handleFinalDelayToggle = async (event) => {
    const disabled = event.target.checked;
    setSavingDelayFlag(true);
    setError(null);
    try {
      await setFeatureFlag(
        FEATURE_FLAG_KEYS.DISABLE_FINAL_POINTS_DELAY,
        disabled,
        "Disable the 12h delay before team points finalise (testing only)."
      );
      setFinalDelayDisabled(disabled);
      setNotice(
        disabled
          ? "12h buffer disabled. Team points now finalise as soon as a match is 'played'."
          : "12h buffer re-enabled. Team points finalise 12h after a match is 'played'."
      );
    } catch {
      setError("Unable to update the scoring buffer setting.");
    } finally {
      setSavingDelayFlag(false);
    }
  };

  useEffect(() => {
    if (!scenarioPlayers.length) return;

    setEventDraft((current) => {
      const playerStillExists = scenarioPlayers.some(
        (player) => player.id === current.playerId
      );
      const nextPlayer = playerStillExists
        ? scenarioPlayers.find((player) => player.id === current.playerId)
        : scenarioPlayers[0];
      const teamStillExists = scenarioTeams.some(
        (team) => team.id === current.teamId
      );

      return {
        ...current,
        playerId: nextPlayer?.id ?? "",
        teamId: teamStillExists
          ? current.teamId
          : nextPlayer?.teamId ?? scenarioTeams[0]?.id ?? "",
        relatedPlayerId: scenarioPlayers.some(
          (player) => player.id === current.relatedPlayerId
        )
          ? current.relatedPlayerId
          : "",
      };
    });
  }, [scenarioPlayers, scenarioTeams]);

  const loadExample = useCallback(async () => {
    setLoadingExample(true);
    setError(null);
    setNotice(null);

    try {
      const body = await get("/admin/match-simulator/example");
      if (!body?.success) {
        throw new Error(
          body?.error?.message || "Unable to load simulator example."
        );
      }
      setScenarioFromObject(getSimulationPayload(body));
      setApplyResult(null);
    } catch {
      setScenarioFromObject(FALLBACK_SCENARIO);
      setNotice("Simulator service not reachable. Local example loaded.");
    } finally {
      setLoadingExample(false);
    }
  }, [setScenarioFromObject]);

  const loadMatches = useCallback(async () => {
    setLoadingMatches(true);
    try {
      const body = await get("/admin/match-simulator/matches?limit=200");
      if (body?.success) {
        setMatches(getSimulationPayload(body) ?? []);
      }
    } catch {
      setMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  }, []);

  const loadGameweeks = useCallback(async (tournamentCalendarId) => {
    if (!tournamentCalendarId) {
      setGameweeks([]);
      return;
    }

    setLoadingGameweeks(true);
    try {
      const body = await get(
        `/admin/match-simulator/gameweeks?tournamentCalendarId=${encodeURIComponent(
          tournamentCalendarId
        )}`
      );
      if (body?.success) {
        setGameweeks(getSimulationPayload(body) ?? []);
      }
    } catch {
      setGameweeks([]);
    } finally {
      setLoadingGameweeks(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccess) return;

    loadCapabilities();
    loadFinalDelayFlag();
  }, [canAccess, loadCapabilities, loadFinalDelayFlag]);

  useEffect(() => {
    if (!canUseSimulator) return;

    loadExample();
    loadMatches();
  }, [canUseSimulator, loadExample, loadMatches]);

  useEffect(() => {
    if (!canUseSimulator) return;

    loadGameweeks(selectedMatchTournamentCalendarId);
    setSelectedGameweekId(selectedMatch?.gameweek_id || "");
    setGameweekDraft((current) => ({
      ...current,
      number: selectedMatch?.gameweek || current.number || "",
      name: selectedMatch?.gameweek
        ? `Gameweek ${selectedMatch.gameweek}`
        : current.name,
    }));
  }, [
    canUseSimulator,
    loadGameweeks,
    selectedMatch?.gameweek,
    selectedMatch?.gameweek_id,
    selectedMatchTournamentCalendarId,
  ]);

  const loadMatchTemplate = async () => {
    if (!selectedMatchId.trim()) {
      setError("Choose or enter a backend match ID first.");
      return;
    }

    setLoadingTemplate(true);
    setError(null);
    setNotice(null);
    setApplyResult(null);

    try {
      const body = await get(
        `/admin/match-simulator/matches/${selectedMatchId.trim()}/template`
      );
      if (!body?.success) {
        throw new Error(
          body?.error?.message || "Unable to load match template."
        );
      }

      const data = getSimulationPayload(body);
      setScenarioFromObject(data?.scenario);
      setNotice(
        "Loaded real match players into the simulator. Edit events, then run or apply."
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to load match template."
      );
    } finally {
      setLoadingTemplate(false);
    }
  };

  const loadLineups = useCallback(async () => {
    if (!effectiveMatchId) return;
    // Only fetch lineups for real DB match IDs (UUIDs), not example scenario IDs like "sim-world-cup-001"
    const UUID_RE =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!UUID_RE.test(effectiveMatchId.trim())) return;
    setLoadingLineups(true);
    try {
      const body = await get(
        `/admin/match-simulator/matches/${effectiveMatchId}/lineups`
      );
      if (body?.success) {
        const rows = getSimulationPayload(body) ?? [];
        const homeTeam = scenarioTeams[0];
        const next = {
          home: {
            formation: "",
            startingXi: [],
            formationPlace: [],
            substitutes: [],
          },
          away: {
            formation: "",
            startingXi: [],
            formationPlace: [],
            substitutes: [],
          },
        };
        for (const row of rows) {
          const target = row.contestantId === homeTeam?.id ? "home" : "away";
          const formation = row.formation || "";
          const startingXi = row.startingXi || [];
          const formationPlace = row.formationPlace || [];

          // Reconstruct slot-based array (length 11) from compact DB array + formationPlace
          const slotArray = new Array(11).fill("");
          const placeArray = new Array(11).fill(0);

          for (let i = 0; i < startingXi.length; i++) {
            const place = formationPlace[i] ?? i + 1;
            const slotIdx = place - 1; // formationPlace is 1-based
            if (slotIdx >= 0 && slotIdx < 11) {
              slotArray[slotIdx] = startingXi[i];
              placeArray[slotIdx] = place;
            }
          }

          next[target] = {
            formation,
            startingXi: slotArray,
            formationPlace: placeArray,
            substitutes: row.substitutes || [],
          };
        }
        setLineups(next);
      }
    } catch {
      // Ignore lineup load errors; admin can build from scratch
    } finally {
      setLoadingLineups(false);
    }
  }, [effectiveMatchId, scenarioTeams]);


  const createAndAssignGameweek = async () => {
    if (!selectedMatchTournamentCalendarId) {
      setError("Choose a recent match before creating a gameweek.");
      return;
    }
    if (!effectiveMatchId) {
      setError("Choose a backend match ID first.");
      return;
    }

    const number = Number(gameweekDraft.number);
    if (!Number.isInteger(number) || number < 1) {
      setError("Gameweek number must be a positive integer.");
      return;
    }

    setSavingGameweek(true);
    setError(null);
    setNotice(null);

    try {
      const createdBody = await post("/admin/match-simulator/gameweeks", {
        tournamentCalendarId: selectedMatchTournamentCalendarId,
        number,
        name: gameweekDraft.name || `Gameweek ${number}`,
        status: gameweekDraft.status,
        budgetInitial: Number(gameweekDraft.budgetInitial) || 100,
      });

      if (!createdBody?.success) {
        throw new Error(
          createdBody?.error?.message || "Unable to create gameweek."
        );
      }

      const gameweek = getSimulationPayload(createdBody);
      const assignBody = await post(
        `/admin/match-simulator/matches/${effectiveMatchId}/gameweek`,
        {
          gameweekId: gameweek.id,
        }
      );

      if (!assignBody?.success) {
        throw new Error(
          assignBody?.error?.message || "Unable to assign match to gameweek."
        );
      }

      setSelectedGameweekId(gameweek.id);
      await Promise.all([
        loadMatches(),
        loadGameweeks(selectedMatchTournamentCalendarId),
      ]);
      setNotice(
        `Match assigned to ${gameweek.name || `Gameweek ${gameweek.number}`}.`
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to set up gameweek."
      );
    } finally {
      setSavingGameweek(false);
    }
  };

  const assignExistingGameweek = async () => {
    if (!effectiveMatchId || !selectedGameweekId) {
      setError("Choose a backend match and gameweek first.");
      return;
    }

    setSavingGameweek(true);
    setError(null);
    setNotice(null);

    try {
      const body = await post(
        `/admin/match-simulator/matches/${effectiveMatchId}/gameweek`,
        {
          gameweekId: selectedGameweekId,
        }
      );

      if (!body?.success) {
        throw new Error(
          body?.error?.message || "Unable to assign match to gameweek."
        );
      }

      await Promise.all([
        loadMatches(),
        loadGameweeks(selectedMatchTournamentCalendarId),
      ]);
      setNotice("Match assigned to the selected gameweek.");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to assign gameweek."
      );
    } finally {
      setSavingGameweek(false);
    }
  };





  useEffect(() => {
    if (!canUseSimulator || !effectiveMatchId) return;
    loadLineups();
  }, [canUseSimulator, effectiveMatchId, loadLineups]);



  const runSimulation = async () => {
    setRunning(true);
    setError(null);
    setNotice(null);

    try {
      if (parsedScenario.error) {
        throw new Error(parsedScenario.error);
      }

      const body = await post("/admin/match-simulator/simulate", scenario);
      if (!body?.success) {
        throw new Error(body?.error?.message || "Simulation failed.");
      }
      setSimulation(getSimulationPayload(body));
      setApplyResult(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Simulation failed.");
    } finally {
      setRunning(false);
    }
  };

  const createSimulatorSession = async () => {
    if (parsedScenario.error) {
      throw new Error(parsedScenario.error);
    }

    const body = await post("/admin/match-simulator/sessions", {
      scenario,
    });
    if (!body?.success) {
      throw new Error(body?.error?.message || "Unable to create session.");
    }

    const nextSession = getSimulationPayload(body);
    updateSessionState(nextSession);
    return nextSession;
  };

  const getLiveSimulatorSession = async (currentSession) => {
    if (!currentSession?.id) return null;

    const body = await get("/admin/match-simulator/sessions");
    if (!body?.success) {
      throw new Error(body?.error?.message || "Unable to load sessions.");
    }

    const sessions = getSimulationPayload(body);
    if (!Array.isArray(sessions)) return currentSession;

    return sessions.find((item) => item?.id === currentSession.id) || null;
  };

  const getOrCreateSimulatorSession = async () => {
    const liveSession = await getLiveSimulatorSession(session);
    if (liveSession) {
      updateSessionState(liveSession);
      return liveSession;
    }

    return createSimulatorSession();
  };




  const clearSessionClick = async () => {
    if (!session?.id) return;

    setSessionWorking(true);
    setError(null);
    setNotice(null);

    try {
      await del(`/admin/match-simulator/sessions/${session.id}`);
      setSession(null);
      setNotice("Simulator session cleared.");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Unable to clear simulator session."
      );
    } finally {
      setSessionWorking(false);
    }
  };


  const applySimulation = async () => {
    setApplying(true);
    setError(null);
    setNotice(null);

    try {
      if (parsedScenario.error) {
        throw new Error(parsedScenario.error);
      }

      const matchId = effectiveMatchId;
      if (!matchId) {
        throw new Error(
          "Choose a backend match ID or use a scenario with match.id."
        );
      }

      const body = await post("/admin/match-simulator/apply", {
        matchId,
        scenario,
        options: {
          markMatchPlayed: true,
          resetExistingStats: true,
          updateMatchScore: true,
        },
      });

      if (!body?.success) {
        throw new Error(body?.error?.message || "Unable to apply simulation.");
      }

      const data = getSimulationPayload(body);
      setApplyResult(data);
      setSimulation(data?.simulation ?? null);
      setNotice(
        "Simulated stats were persisted and backend scoring has been recalculated."
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to apply simulation."
      );
    } finally {
      setApplying(false);
    }
  };

  const resetSimulationData = async () => {
    setResetting(true);
    setError(null);
    setNotice(null);

    try {
      const matchId = effectiveMatchId;
      if (!matchId) {
        throw new Error(
          "Choose a backend match ID or use a scenario with match.id before resetting."
        );
      }

      const body = await post("/admin/match-simulator/reset", {
        matchId,
        scope: resetScope,
      });

      if (!body?.success) {
        throw new Error(
          body?.error?.message || "Unable to reset simulation data."
        );
      }

      const reset = getSimulationPayload(body)?.reset;
      const cleared = reset?.cleared ?? {};
      setApplyResult(null);
      setSimulation(null);
      setNotice(
        `Cleared simulation data (${
          reset?.scope === "match" ? "this match" : "whole matchday"
        }): ${formatNumber(cleared.matchStats)} match stats, ${formatNumber(
          cleared.fantasyPlayerMatchdays
        )} player rows, ${formatNumber(
          cleared.fantasyTeamMatchdaySnapshots
        )} team snapshots, ${formatNumber(
          cleared.matchEvents
        )} events. ${formatNumber(cleared.matchesReset)} match(es) reset to ${
          reset?.matchStatus || "fixture"
        }, ${
          reset?.matchdayStatus
            ? `matchday set to ${reset.matchdayStatus}`
            : "no matchday reset"
        }, ${
          reset?.gameweekStatus
            ? `gameweek set to ${reset.gameweekStatus}`
            : "no gameweek reset"
        }.`
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to reset simulation data."
      );
    } finally {
      setResetting(false);
    }
  };

  const confirmResetClick = async () => {
    setResetDialogOpen(false);
    await resetSimulationData();
  };

  const applyLifecycle = async (payload) => {
    const matchId = payload?.matchId || effectiveMatchId;
    if (!matchId) {
      throw new Error("Choose a backend match ID or load a DB template first.");
    }

    const body = await post("/admin/match-simulator/lifecycle", {
      ...payload,
      matchId,
    });
    if (!body?.success) {
      throw new Error(body?.error?.message || "Unable to update lifecycle.");
    }

    const result = getSimulationPayload(body);
    setLastLifecycleResult(result);
    if (result?.current?.match?.status) {
      setLifecycleDraft((current) => ({
        ...current,
        matchStatus: result.current.match.status,
        matchdayStatus:
          result.current.matchday?.status || current.matchdayStatus,
        gameweekStatus:
          result.current.gameweek?.status || current.gameweekStatus,
      }));
    }
    return result;
  };



  const applyTimedSessionSnapshot = async (currentSession, matchStatus) => {
    const matchId = effectiveMatchId;
    if (!matchId) {
      throw new Error("Choose a backend match ID or load a DB template first.");
    }

    if (!currentSession?.result) {
      throw new Error("No simulator snapshot is available to apply.");
    }

    const isFinal = matchStatus === "played";
    const body = await post("/admin/match-simulator/apply", {
      matchId,
      scenario: currentSession.result,
      options: {
        markMatchPlayed: isFinal,
        matchStatus,
        resetExistingStats: true,
        updateMatchScore: true,
      },
    });

    if (!body?.success) {
      throw new Error(
        body?.error?.message || "Unable to apply scenario snapshot."
      );
    }

    const data = getSimulationPayload(body);
    setApplyResult(data);
    setSimulation(data?.simulation ?? currentSession.result);
    return data;
  };

  const startScenarioTimeline = async () => {
    setScenarioWorking(true);
    setError(null);
    setNotice(null);

    try {
      if (parsedScenario.error) {
        throw new Error(parsedScenario.error);
      }

      const currentSession = await getOrCreateSimulatorSession();
      const body = await post(
        `/admin/match-simulator/sessions/${currentSession.id}/start`,
        {
          minute: 0,
        }
      );
      if (!body?.success) {
        throw new Error(body?.error?.message || "Unable to start scenario.");
      }

      const started = getSimulationPayload(body);
      updateSessionState(started);
      await applyLifecycle({
        matchId: effectiveMatchId,
        matchStatus: "playing",
        kickoffAt: new Date().toISOString(),
      });
      await applyTimedSessionSnapshot(started, "playing");
      setScenarioMinute(0);
      setNotice(
        "Scenario started live at 0'. Backend match is playing and live player points were refreshed."
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to start scenario."
      );
    } finally {
      setScenarioWorking(false);
    }
  };

  const advanceScenarioTimeline = async () => {
    setScenarioWorking(true);
    setError(null);
    setNotice(null);

    try {
      if (parsedScenario.error) {
        throw new Error(parsedScenario.error);
      }

      const duration = scenario?.match?.durationMinutes ?? 90;
      const minute = Math.max(
        0,
        Math.min(Number(scenarioMinute) || 0, duration)
      );
      const currentSession = await getOrCreateSimulatorSession();
      const body = await post(
        `/admin/match-simulator/sessions/${currentSession.id}/status`,
        {
          status: "playing",
          minute,
        }
      );
      if (!body?.success) {
        throw new Error(body?.error?.message || "Unable to advance scenario.");
      }

      const advanced = getSimulationPayload(body);
      updateSessionState(advanced);
      await applyTimedSessionSnapshot(advanced, "playing");
      setScenarioMinute(minute);
      setNotice(
        `Scenario advanced to ${minute}'. Backend live player points were refreshed.`
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to advance scenario."
      );
    } finally {
      setScenarioWorking(false);
    }
  };

  const endScenarioTimeline = async () => {
    setScenarioWorking(true);
    setError(null);
    setNotice(null);

    try {
      if (parsedScenario.error) {
        throw new Error(parsedScenario.error);
      }

      const duration = scenario?.match?.durationMinutes ?? 90;
      const currentSession = await getOrCreateSimulatorSession();
      const body = await post(
        `/admin/match-simulator/sessions/${currentSession.id}/end`,
        {
          minute: duration,
        }
      );
      if (!body?.success) {
        throw new Error(body?.error?.message || "Unable to end scenario.");
      }

      const ended = getSimulationPayload(body);
      updateSessionState(ended);
      await applyTimedSessionSnapshot(ended, "played");
      setScenarioMinute(duration);
      setNotice(
        "Scenario ended. Backend match is played and final team scoring was recalculated."
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to end scenario.");
    } finally {
      setScenarioWorking(false);
    }
  };


  if (!canAccess) {
    return (
      <>
        <Helmet>
          <title>Match Simulator | DreamTeam Admin</title>
        </Helmet>
        <Container maxWidth="xl">
          <Alert severity="error">Super admin access required.</Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Match Simulator | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Box
          sx={{
            mb: 3,
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box>
            <Typography variant="h4">Match Simulator</Typography>
            <Chip
              sx={{ mt: 1 }}
              size="small"
              variant="outlined"
              label="Super-admin scoring test"
            />
          </Box>
          {simulatorTab === "advanced" && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={
                loadingExample ? (
                  <CircularProgress size={16} />
                ) : (
                  <RestartAltIcon />
                )
              }
              onClick={loadExample}
              disabled={
                !canUseSimulator || loadingExample || running || applying
              }
            >
              Load example
            </Button>
            <Button
              variant="contained"
              startIcon={
                running ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PlayArrowIcon />
                )
              }
              onClick={runSimulation}
              disabled={!canUseSimulator || running || applying}
            >
              Run simulation
            </Button>
            <Button
              color="secondary"
              variant="contained"
              startIcon={
                applying ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <StorageIcon />
                )
              }
              onClick={applySimulation}
              disabled={!canApplySimulator || running || applying}
            >
              Apply to backend
            </Button>
          </Stack>
          )}
        </Box>

        <Card sx={{ mb: 3 }}>
          <Tabs
            value={simulatorTab}
            onChange={(_event, value) => setSimulatorTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="tests" label="Scenario tests" />
            <Tab value="replay" label="Season replay" />
            <Tab value="advanced" label="Advanced controls" />
          </Tabs>
        </Card>

        {simulatorTab === "advanced" && (
        <>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Simulator mode
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Keep simulator controls off unless a super admin is running a
                  controlled match test.
                </Typography>
              </Box>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Chip
                  color={canRunSimulator ? "success" : "warning"}
                  variant={canRunSimulator ? "filled" : "outlined"}
                  label={
                    loadingCapabilities
                      ? "Checking backend"
                      : canRunSimulator
                      ? capabilities?.canApplySimulation
                        ? "Backend ready"
                        : "Dry run only"
                      : "Backend not configured"
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={canUseSimulator}
                      onChange={handleSimulatorModeChange}
                      disabled={loadingCapabilities || !canRunSimulator}
                    />
                  }
                  label={canUseSimulator ? "Enabled" : "Disabled"}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Scoring buffer (testing)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  When ON, team points finalise immediately once a match is
                  &quot;played&quot; (skips the 12h buffer). Player points are
                  always live. Global - affects all matches.
                </Typography>
              </Box>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Chip
                  color={finalDelayDisabled ? "warning" : "success"}
                  variant={finalDelayDisabled ? "filled" : "outlined"}
                  label={
                    loadingDelayFlag
                      ? "Loading"
                      : finalDelayDisabled
                      ? "12h buffer OFF"
                      : "12h buffer ON"
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={finalDelayDisabled}
                      onChange={handleFinalDelayToggle}
                      disabled={loadingDelayFlag || savingDelayFlag}
                    />
                  }
                  label={
                    finalDelayDisabled ? "Buffer disabled" : "Buffer enabled"
                  }
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {!canUseSimulator && (
          <Alert severity={canRunSimulator ? "info" : "warning"} sx={{ mb: 3 }}>
            {canRunSimulator
              ? "Simulator mode is off. Enable it to load, run, or apply test match data."
              : "Simulator controls are unavailable because this backend is not configured with a simulator service."}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Backend match ID"
                  size="small"
                  value={selectedMatchId}
                  onChange={(event) => setSelectedMatchId(event.target.value)}
                  helperText="Use a real dev match to persist and score simulated data."
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth size="small">
                  <InputLabel id="recent-match-label">
                    Recent matches
                  </InputLabel>
                  <Select
                    labelId="recent-match-label"
                    label="Recent matches"
                    value=""
                    displayEmpty
                    onChange={(event) => setSelectedMatchId(event.target.value)}
                    renderValue={() =>
                      loadingMatches
                        ? "Loading matches..."
                        : "Choose recent match"
                    }
                  >
                    {matches.map((match) => (
                      <MenuItem key={match.id} value={match.id}>
                        {formatMatchLabel(match)}
                      </MenuItem>
                    ))}
                    {matches.length === 0 && (
                      <MenuItem disabled value="">
                        No matches loaded
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack direction="row" spacing={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={loadMatchTemplate}
                    disabled={
                      !canUseSimulator || loadingTemplate || applying || running
                    }
                    startIcon={
                      loadingTemplate ? (
                        <CircularProgress size={16} />
                      ) : (
                        <RestartAltIcon />
                      )
                    }
                  >
                    Load DB template
                  </Button>
                </Stack>
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              DB templates use real fantasy player IDs, so applying the
              simulation exercises the same backend scoring path that Opta stats
              use.
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Gameweek setup
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 1 }}
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        selectedMatch?.gameweek
                          ? `GW ${selectedMatch.gameweek}`
                          : "No gameweek"
                      }
                    />
                    <Chip
                      size="small"
                      color={
                        selectedMatch?.simulator_locked_at
                          ? "warning"
                          : "default"
                      }
                      variant={
                        selectedMatch?.simulator_locked_at
                          ? "filled"
                          : "outlined"
                      }
                      label={
                        selectedMatch?.simulator_locked_at
                          ? "Simulator locked"
                          : "Opta writable"
                      }
                    />
                  </Stack>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() =>
                    loadGameweeks(selectedMatchTournamentCalendarId)
                  }
                  disabled={
                    !canUseSimulator ||
                    loadingGameweeks ||
                    !selectedMatchTournamentCalendarId
                  }
                >
                  Refresh gameweeks
                </Button>
              </Stack>

              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={3} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="GW number"
                    value={gameweekDraft.number}
                    onChange={(event) =>
                      setGameweekDraft((current) => ({
                        ...current,
                        number: event.target.value,
                      }))
                    }
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={5} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="GW name"
                    value={gameweekDraft.name}
                    onChange={(event) =>
                      setGameweekDraft((current) => ({
                        ...current,
                        name: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="gameweek-status-setup-label">
                      Status
                    </InputLabel>
                    <Select
                      labelId="gameweek-status-setup-label"
                      label="Status"
                      value={gameweekDraft.status}
                      onChange={(event) =>
                        setGameweekDraft((current) => ({
                          ...current,
                          status: event.target.value,
                        }))
                      }
                    >
                      {GAMEWEEK_STATUSES.map((status) => (
                        <MenuItem key={status} value={status}>
                          {titleize(status)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Budget"
                    value={gameweekDraft.budgetInitial}
                    onChange={(event) =>
                      setGameweekDraft((current) => ({
                        ...current,
                        budgetInitial: event.target.value,
                      }))
                    }
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={8} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={createAndAssignGameweek}
                    disabled={
                      !canApplySimulator ||
                      savingGameweek ||
                      !selectedMatchTournamentCalendarId ||
                      !effectiveMatchId
                    }
                    sx={{ height: 40 }}
                  >
                    Create and assign
                  </Button>
                </Grid>
              </Grid>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={9}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="existing-gameweek-label">
                      Existing gameweek
                    </InputLabel>
                    <Select
                      labelId="existing-gameweek-label"
                      label="Existing gameweek"
                      value={selectedGameweekId}
                      onChange={(event) =>
                        setSelectedGameweekId(event.target.value)
                      }
                    >
                      {gameweeks.map((gameweek) => (
                        <MenuItem key={gameweek.id} value={gameweek.id}>
                          GW {gameweek.number} - {gameweek.name || "Unnamed"} -{" "}
                          {titleize(gameweek.status)}
                        </MenuItem>
                      ))}
                      {gameweeks.length === 0 && (
                        <MenuItem disabled value="">
                          {loadingGameweeks
                            ? "Loading gameweeks"
                            : "No gameweeks loaded"}
                        </MenuItem>
                      )}
                    </Select>
                    <FormHelperText>
                      {selectedMatchTournamentCalendarId
                        ? "Dates recalculate from fixtures assigned to this gameweek."
                        : "Choose a recent match to load its tournament gameweeks."}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={assignExistingGameweek}
                    disabled={
                      !canApplySimulator ||
                      savingGameweek ||
                      !selectedGameweekId ||
                      !effectiveMatchId
                    }
                    sx={{ height: 40 }}
                  >
                    Assign selected
                  </Button>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Reset simulation data
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Clear the scoring data a simulation generates (match stats,
                  live player points, team snapshots, match events) and reset
                  the match(es) back to a fixture state so you can re-run
                  cleanly. User squads, picks, captains, transfers, boosters and
                  leagues are preserved.
                </Typography>
              </Box>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ minWidth: { sm: 360 } }}
              >
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="reset-scope-label">Reset scope</InputLabel>
                  <Select
                    labelId="reset-scope-label"
                    label="Reset scope"
                    value={resetScope}
                    onChange={(event) => setResetScope(event.target.value)}
                  >
                    <MenuItem value="matchday">
                      Whole matchday (all matches)
                    </MenuItem>
                    <MenuItem value="match">This match only</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  color="error"
                  variant="outlined"
                  startIcon={
                    resetting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <DeleteSweepIcon />
                    )
                  }
                  onClick={() => setResetDialogOpen(true)}
                  disabled={
                    !canApplySimulator ||
                    resetting ||
                    applying ||
                    running ||
                    !effectiveMatchId
                  }
                >
                  Reset simulation data
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Dialog
          open={resetDialogOpen}
          onClose={() => setResetDialogOpen(false)}
          aria-labelledby="reset-simulation-dialog-title"
        >
          <DialogTitle id="reset-simulation-dialog-title">
            Reset simulation data?
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              This permanently clears the scoring data generated by simulations
              for{" "}
              <strong>
                {resetScope === "match"
                  ? "this match"
                  : "every match in this matchday"}
              </strong>{" "}
              ({effectiveMatchId || "no match selected"}): match stats, live
              player points, team snapshots and match events. The match(es) are
              reset to a fixture state and the matchday to upcoming. User
              squads, captains, transfers, boosters and leagues are not
              affected. This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={confirmResetClick}
              color="error"
              variant="contained"
            >
              Reset data
            </Button>
          </DialogActions>
        </Dialog>

        <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          Scenario timeline
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Uses the Scenario JSON as the full match timeline.
                          Start applies a 0&apos; live snapshot, advance applies
                          all events up to the selected minute, and end applies
                          the full-time snapshot.
                        </Typography>
                      </Box>
                      <Chip
                        color={
                          session?.status === "playing" ? "success" : "default"
                        }
                        label={
                          session
                            ? `${titleize(session.status)} - ${
                                session.currentMinute
                              }'`
                            : "No session"
                        }
                      />
                    </Box>

                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Minute"
                          value={scenarioMinute}
                          onChange={(event) =>
                            setScenarioMinute(event.target.value)
                          }
                          inputProps={{
                            min: 0,
                            max: scenario?.match?.durationMinutes ?? 120,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={10}>
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Tooltip title={parsedScenario.error || ""}>
                            <span>
                              <Button
                                variant="contained"
                                startIcon={
                                  scenarioWorking ? (
                                    <CircularProgress
                                      size={16}
                                      color="inherit"
                                    />
                                  ) : (
                                    <PlayArrowIcon />
                                  )
                                }
                                onClick={startScenarioTimeline}
                                disabled={
                                  !canApplySimulator ||
                                  scenarioWorking ||
                                  Boolean(parsedScenario.error) ||
                                  !effectiveMatchId
                                }
                              >
                                Start live
                              </Button>
                            </span>
                          </Tooltip>
                          <Button
                            variant="outlined"
                            startIcon={<TimelineIcon />}
                            onClick={advanceScenarioTimeline}
                            disabled={
                              !canApplySimulator ||
                              scenarioWorking ||
                              Boolean(parsedScenario.error) ||
                              !effectiveMatchId
                            }
                          >
                            Advance & apply live
                          </Button>
                          <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<StopCircleIcon />}
                            onClick={endScenarioTimeline}
                            disabled={
                              !canApplySimulator ||
                              scenarioWorking ||
                              Boolean(parsedScenario.error) ||
                              !effectiveMatchId
                            }
                          >
                            End & apply final
                          </Button>
                          <Button
                            variant="text"
                            color="inherit"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={clearSessionClick}
                            disabled={
                              !canUseSimulator ||
                              scenarioWorking ||
                              sessionWorking ||
                              !session?.id
                            }
                          >
                            Clear session
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>

                    {session?.id && (
                      <Typography variant="caption" color="text.secondary">
                        Session {session.id}
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

        {notice && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {notice}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} lg={5}>
            <Card sx={{ height: "100%" }}>
              <Box
                sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Scenario JSON
                </Typography>
              </Box>
              <CardContent>
                <TextField
                  fullWidth
                  multiline
                  minRows={28}
                  value={scenarioText}
                  onChange={(event) => setScenarioText(event.target.value)}
                  inputProps={{
                    spellCheck: false,
                    sx: {
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace",
                      fontSize: 13,
                      lineHeight: 1.5,
                    },
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Grid container spacing={3}>
              {metrics.map((metric) => (
                <ResultMetric
                  key={metric.label}
                  label={metric.label}
                  value={metric.value}
                />
              ))}
              <Grid item xs={12} md={5}>
                <ScoreTable simulation={simulation} />
              </Grid>
              <Grid item xs={12} md={7}>
                <FantasyTeamTable
                  teams={simulation?.fantasyTeamResults ?? []}
                />
              </Grid>
              <Grid item xs={12}>
                <PlayerPointsTable players={simulation?.playerResults ?? []} />
              </Grid>
              <Grid item xs={12}>
                <EventsTable events={simulation?.events ?? []} />
              </Grid>
              {applyResult && (
                <Grid item xs={12}>
                  <BackendScoringResult result={applyResult} />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        </>
        )}

        {simulatorTab === "tests" && <ScenarioTestsPanel canRun={canApplySimulator} />}

        {simulatorTab === "replay" && <SeasonReplayPanel canRun={canApplySimulator} />}
      </Container>
    </>
  );
}
